import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { IoChevronBack } from 'react-icons/io5';

import { Input, PasswordEyeInput } from '@/components/forms';
import { Button, Spinner, Card } from '@/components/ui';
import { GoogleSignInButton } from '@/components/features/GoogleSignInButton';
import { useAuthStore } from '@/stores/useAuthStore';
import { ROUTES } from '@/constants/routes';
import { sanitizeEmail } from '@/lib/sanitize';
import { logger } from '@/lib/logger';
import type { LoginRequest, AuthResult } from '@/types/auth';

import authStyles from '../Auth.module.css';
import styles from './Login.module.css';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logIn, googleLogin, isLoading, isLogged, getAccessToken } = useAuthStore();
  const hasValidated = useRef(false);

  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });

  const handleRedirect = useCallback(() => {
    // Check for redirect path in location state
    if (location.state?.from?.pathname) {
      navigate(location.state.from.pathname);
      return;
    }

    // All authenticated users go to dashboard; DashboardIndex handles role-specific redirects
    navigate(ROUTES.dashboard);
  }, [location.state?.from?.pathname, navigate]);

  useEffect(() => {
    if (!isLogged || hasValidated.current) return;
    hasValidated.current = true;

    // Validate the token before redirecting — stale tokens get cleaned up
    getAccessToken().then((token) => {
      if (token) {
        handleRedirect();
      }
      // If token is null, logOut was called by getAccessToken,
      // isLogged becomes false, and the user stays on login
    });
  }, [isLogged, handleRedirect, getAccessToken]);

  const handleFormDataChange = (key: string, value: string) => {
    setFormData((prevState: LoginRequest) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateForm = (formData: LoginRequest) => {
    const { email, password } = formData;

    if (!email || !password) {
      return { isValid: false, message: 'Please complete all fields' };
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    return { isValid: true, message: '' };
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validation = validateForm(formData);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    // Sanitize inputs before sending to API
    const sanitizedData: LoginRequest = {
      email: sanitizeEmail(formData.email),
      password: formData.password, // Don't sanitize passwords
    };

    try {
      const status: AuthResult = await logIn(sanitizedData, () => logger.info('2FA required'));

      if (status === 'success') {
        toast.success('Login successful');
        handleRedirect();
      }

      if (status === 'confirm_email') {
        toast.success('Please confirm your email address');
        navigate(ROUTES.verifyEmail);
      }

      return;
    } catch (error: unknown) {
      toast.error('An error occurred while signing in');
      logger.error('Login error:', error);
    }
  };

  const handleGoogleSuccess = async (code: string) => {
    try {
      const result: AuthResult = await googleLogin(code);
      if (result === 'success') {
        toast.success('Login successful');
        handleRedirect();
      }
    } catch (error: unknown) {
      toast.error('Google sign-in failed');
      logger.error('Google login error:', error);
    }
  };

  return (
    <div className={authStyles.container}>
      <div className={authStyles.header}>
        <button
          className={authStyles.backButton}
          onClick={() => navigate(ROUTES.home)}
          aria-label="Back to home"
        >
          <IoChevronBack /> Back to Home
        </button>
      </div>
      <Card className={authStyles.card}>
        <h1 className={authStyles.title}>Welcome</h1>

        <GoogleSignInButton onSuccess={handleGoogleSuccess} disabled={isLoading} />
        <div className={styles.divider}>
          <span>or</span>
        </div>

        <form className={authStyles.form} onSubmit={handleFormSubmit}>
          <Input
            name="email"
            value={formData.email}
            setValue={(value) => handleFormDataChange('email', value)}
            label="Email Address"
            placeholder="Enter your email"
          />
          <PasswordEyeInput
            name="password"
            value={formData.password}
            setValue={(value) => handleFormDataChange('password', value)}
            label="Password"
            placeholder="Enter your password"
          />

          <Button variant="primary" size="md" type="submit" disabled={isLoading}>
            {isLoading ? <Spinner variant="secondary" size="sm" /> : 'Log In'}
          </Button>
        </form>

        <div className={authStyles.footer}>
          <button className="text-btn" onClick={() => navigate(ROUTES.forgotPassword)}>
            Forgot your password?
          </button>
        </div>

        <div className={authStyles.footer}>
          <span>Don't have an account?</span>
          <button className="text-btn" onClick={() => navigate(ROUTES.register)}>
            <span className={authStyles.link}>Register</span>
          </button>
        </div>
      </Card>
    </div>
  );
}
